import { isAnalyzingAtom } from '@realtime';
import { useAtomValue } from 'jotai';

import { type Unit } from '@core';
import { ActivitySwing } from '@data';
import { useUserSettingsWithDefaults } from '@hooks';
import { useSwingAnalysis } from '@queries';
import { ActivitySidebar, ActivitySidebarItem, Box, colors, Spinner } from '@ui';

import { swingsAtom } from '../../store/activity';

import { getDisplayValue } from './getDisplayValue';

interface Parameter {
    readonly parameterName: string;
    readonly parameterID: string;
    readonly parameterUnit: Unit;
}

type FloorActivityItemProps = ActivitySwing & Parameter & { isAnalyzing: boolean };

const FloorActivityItem = ({ uuid, number, isAnalyzing, parameterID, parameterUnit }: FloorActivityItemProps) => {
    const { data: swingAnalysis } = useSwingAnalysis(uuid);
    const { data: userSettings } = useUserSettingsWithDefaults();
    const displayValue = getDisplayValue(userSettings.userPreferredUnit, parameterID, parameterUnit, swingAnalysis);
    return <ActivitySidebarItem number={number} value={displayValue} isAnalyzing={isAnalyzing} />;
};

export function FloorActivityNavigation(props: Partial<Parameter>) {
    const { parameterID, parameterName, parameterUnit } = props;
    const swings = useAtomValue(swingsAtom);
    const { data: userSettings } = useUserSettingsWithDefaults();
    const isAnalyzing = useAtomValue(isAnalyzingAtom);

    if (!parameterUnit || !parameterName || !parameterID) {
        return (
            <Box display="flex" height="100%" flex={1}>
                <Spinner size="l" color={colors.bluegray[600]} />
            </Box>
        );
    }
    const defaultDisplayValue = getDisplayValue(userSettings.userPreferredUnit, parameterID, parameterUnit, null);

    return (
        <ActivitySidebar fallbackDisplayValue={defaultDisplayValue} selectedKeyParameter={parameterName}>
            {isAnalyzing && (
                <ActivitySidebarItem
                    number={(swings?.length || 0) + 1}
                    value={defaultDisplayValue}
                    isAnalyzing={false}
                />
            )}
            {swings?.map((swing, idx) => {
                return (
                    <FloorActivityItem
                        key={swing.uuid}
                        parameterID={parameterID}
                        parameterName={parameterName}
                        parameterUnit={parameterUnit}
                        {...swing}
                        isAnalyzing={idx === 0}
                    />
                );
            })}
        </ActivitySidebar>
    );
}
